'use client'

import { FC, ReactElement, ReactNode } from 'react'
import useExchangeRate from '@/utils/hooks/useExchangeRate'
import Ticker from '@/components/ticker'
import formatNumber from '@/utils/format-number'
import useFeeEstimates from '@/utils/hooks/useFeeEstimates'
import BitcoinIcon from '@/icons/bitcoin-filled'
import FeeSelector from '@/components/fee-selector'
import { Skeleton } from '@/components/ui/skeleton'
import { cn } from '@/lib/utils'
import { Cog } from 'lucide-react'
import { LanguageToggle } from '@/i18n/provider'
import useApiStatus from '@/utils/hooks/useApiStatus'

import Link from 'next/link'
import TwitterIcon from '@/icons/twitter'
import GitbookIcon from '@/icons/gitbook'
import FooterSubscribe from './subscribe'

/// The site's bottom bar. Also reused inside the full-screen /alkanes app
/// (re-tinted through its own chrome tokens, not re-implemented) — the extra
/// props exist for that embedding and default to the site's exact behavior.
const FooterV2: FC<{
	/// Extra classes for the fixed bar itself (surface re-tints).
	className?: string
	/// The in-flow spacer that reserves the bar's height. Full-screen app
	/// layouts manage their own bottom padding and turn it off.
	spacer?: boolean
	/// Rendered immediately after the BTC price, same visual grammar
	/// (the /alkanes embedding adds the DIESEL price here).
	priceSlot?: ReactNode
	/// Test id for the fixed bar, so an embedding can keep its own pins.
	testId?: string
}> = ({ className, spacer = true, priceSlot, testId }): ReactElement => {
	const { exchangeRate } = useExchangeRate()
	const { feeRate } = useFeeEstimates()

	const loading = !exchangeRate || !feeRate

	const { data: apiStatus, isLoading: apiStatusLoading } = useApiStatus()

	return (
		<>
			{spacer && <div className="h-8" />}
			<div
				data-testid={testId}
				className={cn(
					'fixed h-8 border-t border-border bg-chrome-muted dark:bg-background text-foreground w-full bottom-0 left-0 z-50 flex justify-center',
					className
				)}
			>
				<div className="flex items-center max-w-[--page-width] p-[--page-padding] w-full justify-between">
					<div className="flex items-center shrink-0 sm:shrink sm:w-full">
						{loading && (
							<>
								<div className="text-foreground text-xs border-z flex items-center select-none">
									<BitcoinIcon className="size-4" />
									<Skeleton className="ml-1 h-3 w-[63px]" />
								</div>
								<div className="h-8 w-[1px] bg-border mx-2" />
								<div className="flex items-center">
									<Skeleton className="w-[53px] h-3 hidden sm:block" />
									<div className="h-8 w-[1px] bg-border mx-2 hidden sm:block" />
									<Cog className="size-4" />
								</div>
							</>
						)}
						{!loading && (
							<>
								<div className="text-foreground text-xs border-z flex items-center gap-1 select-none">
									<BitcoinIcon className="size-4" />{' '}
									<Ticker
										value={exchangeRate}
										format={(v) => `$${formatNumber(v.toFixed(2))}`}
										snapBelow={0.00001}
									/>
								</div>
								{priceSlot && (
									<>
										<div className="h-8 w-[1px] bg-border mx-2" />
										{priceSlot}
									</>
								)}
								<div className="h-8 w-[1px] bg-border mx-2" />
								<FeeSelector>
									<div
										className="flex items-center cursor-pointer"
										data-testid="footer-fee-cluster"
									>
										<p className="text-foreground text-xs border-z flex items-center gap-1 select-none whitespace-nowrap hidden sm:flex">
											{Number(feeRate.toFixed(2))} sats/vB
										</p>
										{/* Phones give the fee rate's room to the email sign-up;
										    the gear alone still opens the selector. */}
										<div className="h-8 w-[1px] bg-border mx-2 hidden sm:block" />
										<Cog className="cursor-pointer size-4" />
									</div>
								</FeeSelector>
							</>
						)}
						<div className="h-8 w-[1px] bg-border mx-2" />
					</div>
					<div className="flex items-center gap-2 min-w-0 flex-1 justify-end sm:flex-none">
						<FooterSubscribe className="flex-1 sm:flex-none" />
						{/* The sign-up is capped narrow so the whole footer fits beside it
						    from lg up; below that the status items drop out before it. */}
						<div className="h-8 w-[1px] bg-border hidden md:block" />
						{apiStatusLoading ? (
							<Skeleton className="w-[53px] h-3 hidden md:block" />
						) : (
							<div className="text-muted-foreground text-xs border-z flex items-center gap-1 select-none hidden md:flex whitespace-nowrap">
								Indexed: <p className="font-bold">{apiStatus?.indexer_height}</p> /{' '}
								<p className="font-bold">{apiStatus?.chain_height}</p>
								<div className="h-8 w-[1px] bg-border mx-2" />
							</div>
						)}
						<div className="text-muted-foreground text-xs border-z flex items-center gap-1 select-none hidden min-[880px]:flex whitespace-nowrap">
							Made in USA <span className="text-md">🇺🇸</span>
							<div className="h-8 w-[1px] bg-border mx-2" />
						</div>
						<p className="text-muted-foreground text-xs border-z flex items-center gap-1 select-none hidden lg:flex">
							support@ordinalswallet.com
						</p>
						<Link
							href="/learn"
							data-testid="footer-learn"
							className="text-muted-foreground text-xs select-none hidden xl:flex shrink-0 whitespace-nowrap hover:text-foreground"
						>
							Learn
						</Link>
						<a href="https://blog.ordinalswallet.com/docs" target="_blank" className="hidden sm:block">
							<GitbookIcon className="size-4 stroke-foreground" />
						</a>
						<a href="https://twitter.com/ordinalswallet" target="_blank" className="hidden sm:block">
							<TwitterIcon className="size-4 stroke-foreground" />
						</a>
						<LanguageToggle />
					</div>
				</div>
			</div>
		</>
	)
}

export default FooterV2
